import { EventEmitter, Inject } from '@angular/core';

import { Subscription } from 'rxjs/Subscription';
import * as SVG from 'svg.js';

import { ColorPaletteService
       } from 'app/controls/color-chooser/color-palette.service';
import { ImageQualityRatingService
       } from 'app/controls/image-quality-rating/image-quality-rating.service';
import { GalleryService } from 'app/gallery/gallery.service';
import { ImageProvisioningService } from 'app/gallery/imageprovider.service';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { FontInjectorService } from 'collage-designer/font-injector.service';
import { StageConstants } from 'core/constants';
import { ErrorService } from 'core/error/error.service';
import { LoggingService } from 'core/logging/logging.service';
import { StageUtils } from 'core/utils.stage';
import { DesignStepService } from '../../design-step/design-step.service';
import { ImagePlaceholder } from './image-placeholder';
import { ActionBarDirective } from './overlays/action-bar/action-bar.directive';
import { QualityRatingIconDirective
       } from './overlays/quality-rating-icon/quality-rating-icon.directive';
import { Placeholder, PlaceholderType } from './placeholder';
import { PlaceholderContentService } from './placeholder-content.service';
import { TextPlaceholder } from './text-placeholder';

interface PlaceholdersListItem
{
  domId: string;
  type: PlaceholderType;
  placeholder: TextPlaceholder | ImagePlaceholder;
}

export class PlaceholderService
{
  public placeholderChanged: EventEmitter<Placeholder> = new EventEmitter();

  private placeholders: Array<PlaceholdersListItem> = [];
  private placeholderChangedSubscriptions: Subscription = new Subscription();
  private actionBars: ActionBarDirective;
  private qualityRatingIcons: QualityRatingIconDirective;

  constructor(
      @Inject(ErrorService) private errorService: ErrorService,
      @Inject(LoggingService) private loggingService: LoggingService,
      @Inject(GalleryService) private galleryService: GalleryService,
      @Inject(ImageQualityRatingService) private imageQualityRatingService:
          ImageQualityRatingService,
      @Inject(ImageProvisioningService) private imageProvisioningService,
      @Inject(FontInjectorService) private fontInjectorService,
      @Inject(DesignStepService) private designStepService,
      @Inject(ColorPaletteService) private colorPaletteService,
      @Inject(ToolboxService) private toolboxService,
      @Inject(PlaceholderContentService) private placeholderContentService
  )
  { }

  public async refreshPlaceholders(
      svgReference: SVG.Doc,
      actionBars: ActionBarDirective,
      qualityRatingIcons: QualityRatingIconDirective): Promise<void>
  {
    this.actionBars = actionBars;
    this.qualityRatingIcons = qualityRatingIcons;
    const document = svgReference.node;

    for (let item of this.placeholders) {
      item.placeholder.destructor();
    }
    this.placeholders = [];
    this.cancelChangeSubscriptions();

    // We must determine all placeholders before we create them, otherwise
    // we could accidentally create sub-placeholders. This would lead to errors.
    const images = [].slice
        .call(document.querySelectorAll('image'))
        .filter(element => !StageUtils.isAncestorLinked(
            <Node> StageUtils.findParentBySelector(element, 'svg')))
        .filter(StageUtils.containerHasZoomMode);
    const texts = [].slice
        .call(document.querySelectorAll('text'))
        .filter(element => !StageUtils.isAncestorLinked(
            <Node> StageUtils.findParentBySelector(element, 'svg')))
        .filter(StageUtils.containerHasZoomMode);

    await Promise.all(
        images.map(i => this.createImagePlaceholder(<SVG.Image> SVG.adopt(i)))
          .concat(
              texts.map(t => this.createTextPlaceholder(
                  <SVG.Text> SVG.adopt(t)))));
  }

  private cancelChangeSubscriptions(): void
  {
    this.placeholderChangedSubscriptions.unsubscribe();
    this.placeholderChangedSubscriptions = new Subscription();
  }

  public getChangeablePlaceholders(): Array<PlaceholdersListItem>
  {
    return this.placeholders.filter(p => p.placeholder.isContentChangeable());
  }

  public getImagePlaceholders(onlyChangeable: boolean = true):
      Array<ImagePlaceholder>
  {
    const placeholders =
        onlyChangeable ? this.getChangeablePlaceholders() : this.placeholders;

    return placeholders
      .filter(item => item.type === PlaceholderType.Image)
      .map(item => <ImagePlaceholder> item.placeholder)
      .filter(item => item.element.node.parentNode);
  }

  public getTextPlaceholders(onlyChangeable: boolean = true):
      Array<TextPlaceholder>
  {
    const placeholders =
        onlyChangeable ? this.getChangeablePlaceholders() : this.placeholders;

    return placeholders
      .filter(item => item.type === PlaceholderType.Text)
      .map(item => <TextPlaceholder> item.placeholder);
  }

  public resetPendingPlaceholders(): void
  {
    const pendingPlaceholders = this.placeholders
      .filter(item => item.placeholder.isPending() === true)
      .map(item => item.placeholder);

    for (let item of pendingPlaceholders) {
      item.set(StageConstants.ATTRIBUTE_PENDING, undefined);
    }
  }

  public getPlaceholderById(id: string): TextPlaceholder | ImagePlaceholder
  {
    return this.placeholders
      .filter(item => item.domId === id)
      .map(item => item.placeholder)[0];
  }

  private async createImagePlaceholder(
      svgImage: SVG.Image): Promise<ImagePlaceholder>
  {
    const isLocked =
      !this.designStepService.changeableImagePlaceholdersAvailable();

    const placeholder = new ImagePlaceholder(
        this.errorService,
        this.loggingService,
        this.designStepService,
        this.galleryService,
        this.imageQualityRatingService,
        this.imageProvisioningService,
        this.toolboxService,
        this.placeholderContentService,
        this,
        this.actionBars,
        this.qualityRatingIcons,
        svgImage,
        this.designStepService.getCurrentDesignState().partId,
        isLocked
    );
    await placeholder.setup();

    this.addPlaceholderToPlaceholders(placeholder);
    return placeholder;
  }

  public async createTextPlaceholder(
      svgText: SVG.Text,
      clickAfterModelSet: boolean = false): Promise<TextPlaceholder>
  {
    const placeholder = new TextPlaceholder(
        this.errorService,
        this.loggingService,
        this.designStepService,
        this.galleryService,
        this.fontInjectorService,
        this.colorPaletteService,
        this.toolboxService,
        this.placeholderContentService,
        this,
        this.actionBars,
        svgText,
        this.designStepService.getCurrentDesignState().partId
    );
    await placeholder.setup(clickAfterModelSet);

    this.addPlaceholderToPlaceholders(placeholder);
    return placeholder;
  }

  private addPlaceholderToPlaceholders(placeholder: Placeholder): void
  {
    this.placeholders.push(<PlaceholdersListItem>{
      domId: placeholder.element.id(),
      type: placeholder.type,
      placeholder: placeholder
    });
    this.placeholderChangedSubscriptions.add(
        placeholder.changed.subscribe(
            () => this.placeholderChanged.emit(placeholder)));
    this.placeholderChangedSubscriptions.add(
        placeholder.destroyed.subscribe(
            () => this.placeholders = this.placeholders.filter(
                pItem => pItem.placeholder !== placeholder)));
  }

  public getUnchangedPlaceholders(onlyChangeable: boolean = true):
      Array<PlaceholdersListItem>
  {
    if (onlyChangeable) {
      return this.placeholders.filter(p =>
          p.placeholder.isUnchanged() && p.placeholder.isContentChangeable());
    }
    else {
      return this.placeholders.filter(p => p.placeholder.isUnchanged());
    }
  }

  public blurAllPlaceholders(force: boolean = false): void
  {
    for (const pItem of this.placeholders) {
      pItem.placeholder.blur(force);
    }
  }

  public hasTransformHandle(): boolean
  {
    return this.placeholders.some(
        pItem => pItem.placeholder.hasTransformHandle());
  }

  public disableTextPlaceholders(): void
  {
    for (let tph of this.getTextPlaceholders(true)) {
      tph.blur(true);
      tph.suspendPointerEvents();
    }
  }

  public enableTextPlaceholders(): void
  {
    for (let tph of this.getTextPlaceholders(true)) {
      tph.resumePointerEvents();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/collage-designer/stage/placeholder/placeholder.service.ts